import { useEffect, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch } from "src/store/store";
import {
  generatedApi,
  useGetRailStationsQuery,
  useGetRatesToMillsQuery,
} from "src/store/api/generatedApi";
import type {
  RailStation,
  MillInfo,
  RatesToMillsResponse,
  TruckRoute,
  UsState,
} from "src/store/api/generatedApi";

const EMPTY_STATIONS: RailStation[] = [];
const EMPTY_MILLS: MillInfo[] = [];
const EMPTY_TRUCK_ROUTES: Record<string, TruckRoute> = {};

interface UseStationsResult {
  stations: RailStation[];
  mills: MillInfo[];
  isFetching: boolean;
  error: string | null;
}

export const useStations = (
  tenantGroupName: string | undefined
): UseStationsResult => {
  const { data, isFetching, error } = useGetRailStationsQuery({
    tenantGroupName,
  });

  return {
    stations: data?.stations ?? EMPTY_STATIONS,
    mills: data?.mills ?? EMPTY_MILLS,
    isFetching,
    error: error ? "logisticsErrorLoadingStations" : null,
  };
};

interface UseRatesToMillsResult {
  ratesData: RatesToMillsResponse | null;
  isFetching: boolean;
  error: string | null;
}

export const useRatesToMills = (
  tenantGroupName: string | undefined,
  stationName: string | null,
  stationState: UsState | null
): UseRatesToMillsResult => {
  const queryArg =
    stationName && stationState
      ? { tenantGroupName, stationName, stationState }
      : skipToken;
  const { data, isFetching, error } = useGetRatesToMillsQuery(queryArg);

  return {
    ratesData: isFetching ? null : (data ?? null),
    isFetching,
    error: error ? "logisticsErrorLoadingRates" : null,
  };
};

interface UseTruckRoutesResult {
  truckRoutes: Record<string, TruckRoute>;
  isLoading: boolean;
  error: string | null;
}

export const useTruckRoutes = (
  stationName: string | null,
  stationState: string | null,
  mills: MillInfo[]
): UseTruckRoutesResult => {
  const dispatch = useAppDispatch();
  const [truckRoutes, setTruckRoutes] = useState<Record<string, TruckRoute>>(
    {}
  );
  const [routesLoading, setRoutesLoading] = useState(false);
  const generationRef = useRef(0);

  const shouldFetch = Boolean(stationName && stationState && mills.length > 0);

  useEffect(() => {
    if (!stationName || !stationState || mills.length === 0) {
      ++generationRef.current;
      return;
    }

    const generation = ++generationRef.current;
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- async data loading state machine
    setRoutesLoading(true);

    const subscriptions = mills.map((mill) =>
      dispatch(
        generatedApi.endpoints.findTruckRoute.initiate(
          {
            stationName,
            stationState,
            destinationLatitude: mill.location.latitude,
            destinationLongitude: mill.location.longitude,
          },
          { subscribe: true, forceRefetch: false }
        )
      )
    );

    void Promise.all(
      mills.map((mill, i) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        subscriptions[i]!.unwrap()
          .then((route) => ({ tenantName: mill.tenant_name, route }))
          .catch(() => ({ tenantName: mill.tenant_name, route: null }))
      )
    ).then((results) => {
      if (generationRef.current !== generation) return;

      setTruckRoutes(
        Object.fromEntries(
          results
            .filter((r) => r.route != null)
            .map((r) => [r.tenantName, r.route])
        )
      );
      setRoutesLoading(false);
    });

    return () => {
      subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    };
  }, [stationName, stationState, mills, dispatch]);

  return {
    truckRoutes: shouldFetch ? truckRoutes : EMPTY_TRUCK_ROUTES,
    isLoading: shouldFetch && routesLoading,
    error: null,
  };
};
